import React from 'react';
import { ChevronLeft, ChevronRight, Plus, Search, X, Grid3X3, List, Calendar, Clock, MapPin, Pencil, Trash2, Link as LinkIcon, CalendarPlus, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { DAYS, MONTHS, EVENT_TYPES, getEventTypeInfo } from './caseConstants';
import { API } from '../../config/api';
import { formatTime } from '../../utils/dateFormat';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/**
 * Calendar component for case appointments
 * Handles both calendar grid view and list view with search functionality
 */
function CaseCalendar({
  appointments,
  currentDate,
  setCurrentDate,
  selectedDate,
  setSelectedDate,
  calendarSearchQuery,
  setCalendarSearchQuery,
  calendarViewMode,
  setCalendarViewMode,
  isStaffOrAdmin,
  onCreateEvent,
  onEditEvent,
  onDeleteEvent,
  parseAppointmentDate, // Function to parse appointment dates (handles ISO strings)
}) {
  // State for calendar links
  const [calendarLinks, setCalendarLinks] = React.useState({});
  const [loadingLinks, setLoadingLinks] = React.useState({});

  // Fetch calendar links for an appointment
  const fetchCalendarLinks = async (appointmentId) => {
    if (calendarLinks[appointmentId] || loadingLinks[appointmentId]) return;
    
    setLoadingLinks(prev => ({ ...prev, [appointmentId]: true }));
    try {
      const response = await fetch(`${API}/appointments/${appointmentId}/calendar-links`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCalendarLinks(prev => ({ ...prev, [appointmentId]: data }));
      }
    } catch (error) {
      console.error('Failed to fetch calendar links:', error);
    } finally {
      setLoadingLinks(prev => ({ ...prev, [appointmentId]: false }));
    }
  };

  // Open calendar link in new tab
  const openCalendarLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Download ICS file
  const downloadICS = async (appointmentId, title) => {
    try {
      const response = await fetch(`${API}/appointments/${appointmentId}/download.ics`, {
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title.replace(/\s+/g, '_')}.ics`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Failed to download ICS:', error);
    }
  };

  // Helper functions
  const formatAppointmentTime = (dateStr) => {
    return formatTime(dateStr);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const isToday = (date) => {
    if (!date) return false;
    return date.toDateString() === new Date().toDateString();
  };

  const isPast = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getAppointmentTypeColor = (type) => {
    const eventType = EVENT_TYPES.find(t => t.value === type);
    return eventType ? eventType.color : 'bg-gray-500 text-white';
  };

  const getAppointmentTypeLabel = (type) => {
    const eventType = EVENT_TYPES.find(t => t.value === type);
    return eventType ? eventType.label : type;
  };

  // Filter appointments based on search query
  const filteredAppointments = React.useMemo(() => {
    if (!calendarSearchQuery.trim()) return appointments;
    
    const query = calendarSearchQuery.toLowerCase();
    return appointments.filter(apt => {
      const title = (apt.title || '').toLowerCase();
      const location = (apt.location || '').toLowerCase();
      const notes = (apt.notes || '').toLowerCase();
      const type = getAppointmentTypeLabel(apt.appointment_type).toLowerCase();
      return title.includes(query) || 
             location.includes(query) || 
             notes.includes(query) ||
             type.includes(query);
    });
  }, [appointments, calendarSearchQuery]);

  // Get filtered events for a specific date
  const getFilteredEventsForDate = React.useCallback((date) => {
    if (!date) return [];
    const targetYear = date.getFullYear();
    const targetMonth = date.getMonth();
    const targetDay = date.getDate();
    
    return filteredAppointments.filter(apt => {
      const aptDate = parseAppointmentDate(apt.date);
      return aptDate.getFullYear() === targetYear &&
             aptDate.getMonth() === targetMonth &&
             aptDate.getDate() === targetDay;
    });
  }, [filteredAppointments, parseAppointmentDate]);

  // Get all events sorted by date for list view
  const allEventsSorted = React.useMemo(() => {
    return [...filteredAppointments].sort((a, b) => 
      parseAppointmentDate(a.date) - parseAppointmentDate(b.date)
    );
  }, [filteredAppointments, parseAppointmentDate]);

  // Group events by date for list view
  const eventsByDate = React.useMemo(() => {
    const groups = {};
    allEventsSorted.forEach(apt => {
      const date = parseAppointmentDate(apt.date);
      const dateKey = date.toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = { date, events: [] };
      }
      groups[dateKey].events.push(apt);
    });
    return Object.values(groups);
  }, [allEventsSorted, parseAppointmentDate]);

  // Auto-switch to list view when searching
  React.useEffect(() => {
    if (calendarSearchQuery.trim() && filteredAppointments.length > 0) {
      setCalendarViewMode('list');
    }
  }, [calendarSearchQuery, filteredAppointments.length, setCalendarViewMode]);

  return (
    <div data-testid="calendar-content">
      {/* Search Bar */}
      <div className="relative w-full mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search events by title, location, or type..."
          value={calendarSearchQuery}
          onChange={(e) => setCalendarSearchQuery(e.target.value)}
          className="w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#80298F] focus:border-transparent"
          data-testid="case-calendar-search"
        />
        {calendarSearchQuery && (
          <button
            onClick={() => setCalendarSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search Results Info */}
      {calendarSearchQuery && (
        <div className="mb-4 p-3 bg-[#80298F]/10 border border-[#80298F]/20 rounded-lg flex items-center justify-between">
          <p className="text-sm text-[#80298F]">
            Found {filteredAppointments.length} event{filteredAppointments.length !== 1 ? 's' : ''} matching &quot;{calendarSearchQuery}&quot;
          </p>
          <button
            onClick={() => setCalendarSearchQuery('')}
            className="text-[#80298F] hover:text-[#6b2278] text-sm font-medium"
          >
            Clear
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => setCurrentDate(new Date())}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold ml-4">
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <button
              onClick={() => setCalendarViewMode('calendar')}
              className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
                calendarViewMode === 'calendar'
                  ? 'bg-[#80298F] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="case-view-calendar"
              title="Calendar View"
            >
              <Grid3X3 className="h-4 w-4" />
              <span className="hidden sm:inline">Calendar</span>
            </button>
            <button
              onClick={() => setCalendarViewMode('list')}
              className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
                calendarViewMode === 'list'
                  ? 'bg-[#80298F] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="case-view-list"
              title="List View"
            >
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">List</span>
            </button>
          </div>

          {isStaffOrAdmin && (
            <Button 
              onClick={() => onCreateEvent()} 
              className="bg-[#80298F] hover:bg-[#6b2278]"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Event
            </Button>
          )}
        </div>
      </div>

      {calendarViewMode === 'calendar' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" key={`case-calendar-${calendarSearchQuery}`}>
          {/* Calendar Grid */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAYS.map(day => (
                  <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth(currentDate).map((date, index) => {
                  if (!date) {
                    return <div key={index} className="aspect-square"></div>;
                  }

                  const dayEvents = getFilteredEventsForDate(date);
                  const hasEvents = dayEvents.length > 0;

                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedDate(date)}
                      onDoubleClick={() => isStaffOrAdmin && onCreateEvent(date)}
                      className={`aspect-square p-1 rounded-lg border transition-all hover:border-[#80298F] ${
                        isToday(date)
                          ? 'bg-[#80298F] text-white border-[#80298F]'
                          : isPast(date)
                          ? 'bg-gray-50 text-gray-400 border-gray-200'
                          : 'bg-white border-gray-200'
                      } ${
                        selectedDate?.toDateString() === date.toDateString()
                          ? 'ring-2 ring-[#80298F]'
                          : ''
                      }`}
                      title={isStaffOrAdmin ? 'Double-click to create event' : ''}
                    >
                      <div className="text-sm font-semibold">{date.getDate()}</div>
                      {hasEvents && (
                        <div className="flex flex-col gap-0.5 mt-1">
                          <div className={`w-full h-1 rounded ${isToday(date) ? 'bg-white' : 'bg-[#FAA519]'}`}></div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded bg-[#FAA519]"></div>
                  <span>Has Events</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded bg-[#80298F]"></div>
                  <span>Today</span>
                </div>
              </div>
            </div>
          </div>

          {/* Events List for Selected Day */}
          <div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">
                  {selectedDate 
                    ? `${MONTHS[selectedDate.getMonth()]} ${selectedDate.getDate()}`
                    : 'Select a date'
                  }
                </h3>
                {isStaffOrAdmin && selectedDate && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => onCreateEvent(selectedDate)}
                    className="text-[#80298F] hover:text-[#6b2278]"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {selectedDate ? (
                <div className="space-y-3">
                  {(() => {
                    const dayEvents = getFilteredEventsForDate(selectedDate);
                    
                    if (dayEvents.length === 0) {
                      return (
                        <div className="text-center py-6 text-gray-500">
                          <Calendar className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">{calendarSearchQuery ? 'No matching events' : 'No events'}</p>
                          {isStaffOrAdmin && !calendarSearchQuery && (
                            <Button 
                              variant="link" 
                              onClick={() => onCreateEvent(selectedDate)}
                              className="text-[#80298F] mt-2 text-sm"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Event
                            </Button>
                          )}
                        </div>
                      );
                    }

                    return dayEvents.map(apt => (
                      <div key={apt.appointment_id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">{apt.title}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              <Clock className="h-3 w-3 inline mr-1" />
                              {formatAppointmentTime(apt.date)}
                            </p>
                            {apt.location && (
                              <p className="text-xs text-gray-500 mt-1">
                                {apt.location.startsWith('http') ? (
                                  <a 
                                    href={apt.location} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-[#80298F] hover:underline flex items-center"
                                  >
                                    <LinkIcon className="h-3 w-3 inline mr-1" />
                                    Meeting Link
                                  </a>
                                ) : (
                                  <>
                                    <MapPin className="h-3 w-3 inline mr-1" />
                                    {apt.location}
                                  </>
                                )}
                              </p>
                            )}
                            {apt.notes && (
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{apt.notes}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2 ml-2">
                            <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${getAppointmentTypeColor(apt.appointment_type)}`}>
                              {getAppointmentTypeLabel(apt.appointment_type)}
                            </span>
                            <div className="flex gap-1">
                              {/* Add to Calendar Dropdown */}
                              <DropdownMenu onOpenChange={(open) => open && fetchCalendarLinks(apt.appointment_id)}>
                                <DropdownMenuTrigger asChild>
                                  <button 
                                    className="text-gray-400 hover:text-[#80298F] transition-colors"
                                    title="Add to Calendar"
                                    data-testid={`add-to-calendar-${apt.appointment_id}`}
                                  >
                                    <CalendarPlus className="h-4 w-4" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  {loadingLinks[apt.appointment_id] ? (
                                    <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                                  ) : calendarLinks[apt.appointment_id] ? (
                                    <>
                                      <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].google_calendar)}>
                                        <ExternalLink className="h-4 w-4 mr-2" />
                                        Google Calendar
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].outlook)}>
                                        <ExternalLink className="h-4 w-4 mr-2" />
                                        Outlook.com
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].yahoo)}>
                                        <ExternalLink className="h-4 w-4 mr-2" />
                                        Yahoo Calendar
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => downloadICS(apt.appointment_id, apt.title)}>
                                        <Calendar className="h-4 w-4 mr-2" />
                                        Download .ics
                                      </DropdownMenuItem>
                                    </>
                                  ) : (
                                    <div className="px-3 py-2 text-sm text-gray-500">Click to load options</div>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                              {isStaffOrAdmin && (
                                <>
                                  <button 
                                    onClick={() => onEditEvent(apt)}
                                    className="text-gray-400 hover:text-[#80298F] transition-colors"
                                    title="Edit event"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button 
                                    onClick={() => onDeleteEvent(apt.appointment_id)}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                    title="Delete event"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <Calendar className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Click a date to view events</p>
                </div>
              )}
            </div>

            {/* Upcoming Events Summary */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mt-4">
              <h3 className="font-semibold text-gray-900 text-sm mb-3">This Month</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Events</span>
                  <span className="font-semibold">{filteredAppointments.filter(a => {
                    const aptDate = parseAppointmentDate(a.date);
                    return aptDate.getMonth() === currentDate.getMonth() && 
                           aptDate.getFullYear() === currentDate.getFullYear();
                  }).length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
            <List className="h-5 w-5 text-[#80298F]" />
            <h3 className="font-semibold text-gray-900">All Events</h3>
            <span className="text-sm font-normal text-gray-500">
              ({filteredAppointments.length} event{filteredAppointments.length !== 1 ? 's' : ''})
            </span>
          </div>

          {eventsByDate.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg font-medium">No events found</p>
              <p className="text-sm mt-1">
                {calendarSearchQuery ? 'Try adjusting your search terms' : 'No events scheduled for this case'}
              </p>
              {isStaffOrAdmin && !calendarSearchQuery && (
                <Button 
                  onClick={() => onCreateEvent()}
                  className="mt-4 bg-[#80298F] hover:bg-[#6b2278]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Event
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {eventsByDate.map((group, groupIndex) => (
                <div key={groupIndex} className="border-b border-gray-100 pb-6 last:border-b-0 last:pb-0">
                  {/* Date Header */}
                  <div className={`flex items-center gap-3 mb-4 pb-2 border-b ${
                    isToday(group.date) ? 'border-[#80298F]' : 'border-gray-200'
                  }`}>
                    <div className={`flex flex-col items-center justify-center w-14 h-14 rounded-lg ${
                      isToday(group.date) 
                        ? 'bg-[#80298F] text-white' 
                        : isPast(group.date) 
                        ? 'bg-gray-100 text-gray-500' 
                        : 'bg-gray-50 text-gray-900'
                    }`}>
                      <span className="text-xs uppercase">{DAYS[group.date.getDay()]}</span>
                      <span className="text-xl font-bold">{group.date.getDate()}</span>
                    </div>
                    <div>
                      <p className={`font-semibold ${isToday(group.date) ? 'text-[#80298F]' : 'text-gray-900'}`}>
                        {MONTHS[group.date.getMonth()]} {group.date.getDate()}, {group.date.getFullYear()}
                      </p>
                      <p className="text-sm text-gray-500">
                        {group.events.length} event{group.events.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {isToday(group.date) && (
                      <span className="ml-auto px-2 py-1 text-xs font-medium bg-[#80298F] text-white rounded">
                        Today
                      </span>
                    )}
                  </div>

                  {/* Events for this date */}
                  <div className="space-y-3 ml-0 sm:ml-[74px]">
                    {group.events.map(apt => (
                      <div key={apt.appointment_id} className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-[#80298F]/30 transition-colors">
                        <div className={`flex-shrink-0 w-1 h-full min-h-[60px] rounded-full ${getAppointmentTypeColor(apt.appointment_type).split(' ')[0]}`}></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">{apt.title}</p>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3.5 w-3.5" />
                                  {formatAppointmentTime(apt.date)}
                                </span>
                                {apt.location && (
                                  <span className="flex items-center gap-1">
                                    {apt.location.startsWith('http') ? (
                                      <a 
                                        href={apt.location} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-[#80298F] hover:underline flex items-center gap-1"
                                      >
                                        <LinkIcon className="h-3.5 w-3.5" />
                                        Meeting Link
                                      </a>
                                    ) : (
                                      <>
                                        <MapPin className="h-3.5 w-3.5" />
                                        <span className="truncate max-w-[150px]">{apt.location}</span>
                                      </>
                                    )}
                                  </span>
                                )}
                              </div>
                              {apt.notes && (
                                <p className="text-sm text-gray-600 mt-2 line-clamp-2">{apt.notes}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${getAppointmentTypeColor(apt.appointment_type)}`}>
                                {getAppointmentTypeLabel(apt.appointment_type)}
                              </span>
                              <div className="flex items-center gap-1">
                                {/* Add to Calendar Dropdown */}
                                <DropdownMenu onOpenChange={(open) => open && fetchCalendarLinks(apt.appointment_id)}>
                                  <DropdownMenuTrigger asChild>
                                    <button 
                                      className="p-1 text-gray-400 hover:text-[#80298F] transition-colors"
                                      title="Add to Calendar"
                                      data-testid={`add-to-calendar-list-${apt.appointment_id}`}
                                    >
                                      <CalendarPlus className="h-4 w-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48">
                                    {loadingLinks[apt.appointment_id] ? (
                                      <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                                    ) : calendarLinks[apt.appointment_id] ? (
                                      <>
                                        <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].google_calendar)}>
                                          <ExternalLink className="h-4 w-4 mr-2" />
                                          Google Calendar
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].outlook)}>
                                          <ExternalLink className="h-4 w-4 mr-2" />
                                          Outlook.com
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].yahoo)}>
                                          <ExternalLink className="h-4 w-4 mr-2" />
                                          Yahoo Calendar
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => downloadICS(apt.appointment_id, apt.title)}>
                                          <Calendar className="h-4 w-4 mr-2" />
                                          Download .ics
                                        </DropdownMenuItem>
                                      </>
                                    ) : (
                                      <div className="px-3 py-2 text-sm text-gray-500">Click to load options</div>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                {isStaffOrAdmin && (
                                  <>
                                    <button 
                                      onClick={() => onEditEvent(apt)}
                                      className="p-1 text-gray-400 hover:text-[#80298F] transition-colors"
                                      title="Edit event"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </button>
                                    <button 
                                      onClick={() => onDeleteEvent(apt.appointment_id)}
                                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                      title="Delete event"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { CaseCalendar };
export default CaseCalendar;
